const en = {
  translation: {
    app: { title: "Live Room" },
    join: {
      title: "Join Live Room",
      description: "Find your people, create a room, and share your screen or camera.",
      displayName: "Display name",
      placeholder: "Your name",
      submit: "Join room",
    },
    auth: {
      suspended: {
        title: "Your account is suspended",
        description:
          "This account no longer has access to Live Room. You can return to sign-in to use another account.",
      },
      signupsClosed: {
        title: "Sign-ups are closed",
        description:
          "New accounts are not being accepted right now. Please try again later. If you already have an account, you can still sign in.",
      },
      backToSignIn: "Back to sign-in",
      checkingSession: "Checking your session…",
      signingIn: "Signing you in…",
      privacy:
        "The app uses your Google account ID, name, photo, and verified email. Your email is not shown to other participants.",
      continueWithGoogle: "Continue with Google",
      developmentBadge: "Development",
      developmentTitle: "Mock Google account",
      developmentDescription: "Create a local test identity for this browser tab.",
      developmentPrivacy:
        "Development identities stay on this local server and each tab can use a different profile.",
      testEmail: "Test email",
      testEmailHint: "Automatically generated from your name for this test account.",
      googleAccount: "Google account",
      mockAccount: "Development test account",
      accountMenu: "Account menu",
      signOut: "Sign out",
      notificationSounds: "Notification sounds",
      errors: {
        default: "Could not sign in. Please try again.",
        "authentication-failed": "Google sign-in could not be verified.",
        "google-script-unavailable":
          "Google sign-in could not be loaded. Check your connection and try again.",
        "auth-not-configured": "Google sign-in is not configured on this server.",
        "invalid-google-credential": "Google could not verify this account. Please try again.",
        "invalid-development-profile": "Enter a valid display name.",
      },
    },
    stage: {
      waitingTitle: "Waiting for people",
      waitingDescription: "Participant screens and cameras will appear here.",
      participants: "Room participants",
      connecting: "Connecting...",
      roomControls: "Room controls",
    },
    callBar: {
      label: "Call in progress",
      return: "Return to call",
      switchTitle: "Leave the current call?",
      switchBody: "You’re still in {{name}}. Joining another room ends that call.",
      switchBodyUnnamed: "Joining another room ends your current call.",
      stay: "Stay",
      switchConfirm: "Leave and join",
    },
    context: {
      watchParticipantScreen: "Watch {{name}}'s screen",
      participantAvatar: "{{name}}",
    },
    updates: {
      available: "Update available",
      ready: "Reload when convenient to use the latest version.",
      sharing: "Updating now will reload the page and stop your current share.",
      watching: "Updating now will reload the page and interrupt what you are watching.",
      apply: "Update now",
      dismiss: "Dismiss update",
    },
    common: {
      camera: "Camera",
      microphone: "Microphone",
      screen: "Screen",
      screenShare: "Screen share",
      inRoom: "In room",
      live: "LIVE",
      cancel: "Cancel",
      continue: "Continue",
      applyChanges: "Apply changes",
      close: "Close",
      unavailable: "Unavailable",
      collecting: "Collecting",
      measuring: "Measuring",
      automatic: "Automatic",
      native: "Native",
      person_one: "{{count}} person",
      person_other: "{{count}} people",
      viewer_one: "{{count}} viewer",
      viewer_other: "{{count}} viewers",
      spectator_one: "{{count}} spectator",
      spectator_other: "{{count}} spectators",
      p2pLink_one: "{{count}} P2P link",
      p2pLink_other: "{{count}} P2P links",
      sfuConnection_one: "{{count}} SFU connection",
      sfuConnection_other: "{{count}} SFU connections",
      meSuffix: "{{name}} (me)",
      you: "You",
    },
    camera: {
      setup: "Set up camera",
      options: "Camera options",
      active: "Camera is on",
      adjust: "Adjust camera",
      adjustDescription: "Change quality without stopping",
      stop: "Turn camera off",
      stopDescription: "End your camera stream",
      settingsDescription: "You can adjust these settings again while the camera is active.",
      qualityTitle: "Video quality",
      qualityDescription: "Balanced defaults work well for most connections.",
      started: "Camera started",
      stopped: "Camera stopped",
      stoppedTitle: "Camera was turned off",
      watch: "Watch Camera",
    },
    voice: {
      setup: "Set up microphone",
      options: "Microphone options",
      active: "Microphone is on",
      adjust: "Adjust microphone",
      adjustDescription: "Change the input device or voice processing",
      stop: "Turn microphone off",
      stopDescription: "Stop sending your voice",
      settingsTitle: "Microphone",
      settingsDescription: "Choose the input settings used when you turn the microphone on.",
      inputTitle: "Input",
      inputDescription: "Choose which microphone the browser should capture.",
      inputDevice: "Input device",
      defaultDevice: "System default",
      defaultBadge: "Default",
      unnamedDevice: "Microphone {{number}}",
      processingTitle: "Voice processing",
      processingDescription: "Choose how your microphone audio is cleaned up.",
      echoCancellation: "Echo cancellation",
      echoCancellationDescription:
        "Reduces sound from speakers being captured again by the microphone.",
      noiseSuppression: "Noise suppression",
      noiseSuppressionDescription:
        "Identify constant noise and keyboard sounds, then reduce them while you speak.",
      autoGainControl: "Automatic gain",
      autoGainControlDescription: "Keeps quiet and loud speech at a more consistent level.",
      threshold: "Sensitivity",
      moreSensitive: "More sensitive",
      lessSensitive: "Less sensitive",
      started: "Microphone started",
      stopped: "Microphone stopped",
      startFailed: "Could not start the microphone: {{error}}",
      settingsFailed: "Could not apply microphone settings: {{error}}",
      permissionPending: "Waiting for microphone permission",
      permissionRequired: "Microphone access is required to enable this control",
      permissionUnavailableWithError: "Microphone access is unavailable: {{error}}",
      blockedTip:
        "Your browser blocked the microphone. Allow it in the site settings (next to the address bar) to talk. You can still listen to everyone.",
      notFoundTip:
        "No microphone was found. Connect a microphone or choose another device in microphone settings, then try again.",
      inUseTip:
        "Your microphone is busy or unavailable to the system. Close other apps using it or check your system audio settings, then try again.",
      failedTip:
        "The microphone could not start. Try again or choose another device in microphone settings. You can still listen to everyone.",
      playbackBlocked:
        "Voice from {{name}} is ready, but the browser requires a click before it can play audio.",
      enableRoomAudio: "Enable room audio",
      muteOtherVoices: "Mute other participant voices",
      unmuteOtherVoices: "Unmute other participant voices",
      participantAudioControls: "Participant microphone controls",
      participantVolume: "Voice volume",
      muteParticipant: "Mute microphone",
      unmuteParticipant: "Unmute microphone",
      participantMicrophoneUnavailable: "This participant is not sharing their microphone",
      notSending: "Microphone off",
      notListeningToOthers: "Not listening to other participants",
    },
    screen: {
      setup: "Set up screen sharing",
      share: "Share screen",
      options: "Screen sharing options",
      active: "Screen is being shared",
      adjust: "Adjust stream",
      adjustDescription: "Change quality and behavior",
      stop: "Stop sharing",
      stopDescription: "End your screen stream",
      settingsTitle: "Screen sharing",
      settingsDescription: "Choose a quality that fits what you’re sharing and your connection.",
      started: "Screen sharing started",
      stopped: "Screen sharing stopped",
      ownAudioWarningTitle: "Room audio may be shared",
      ownAudioWarningDescription:
        "This browser cannot exclude room voices from system-audio capture. Other participants may hear themselves; use headphones to prevent echo.",
      dismissOwnAudioWarning: "Dismiss audio capture warning",
      pictureTitle: "Picture",
      pictureDescription: "Higher values look sharper but use more upload bandwidth.",
      stoppedTitle: "Screen sharing stopped",
      watch: "Watch Stream",
    },
    settings: {
      resolution: "Resolution",
      frameRate: "Frame rate",
      optimizeFor: "Optimize for",
      cameraResolution: "Camera resolution",
      cameraFrameRate: "Camera frame rate",
      screenResolution: "Screen resolution",
      screenFrameRate: "Screen frame rate",
      contentOptimization: "Content optimization",
      auto: "Auto",
      detail: "Graphics",
      text: "Text",
      motion: "Motion",
      nativeAutomatic: "Native / automatic",
      browserManaged: "Browser managed",
      resourceDemand: "Estimated resource demand",
      resourceDemandLow: "Low",
      resourceDemandLowDescription:
        "{{resolution}} at {{fps}} FPS should create a light encoding workload and modest upload demand.",
      resourceDemandModerate: "Moderate",
      resourceDemandModerateDescription:
        "{{resolution}} at {{fps}} FPS should create a balanced encoding workload and upload demand.",
      resourceDemandHigh: "High",
      resourceDemandHighDescription:
        "{{resolution}} at {{fps}} FPS may noticeably increase encoding load and upload usage.",
      resourceDemandVeryHigh: "Very high",
      resourceDemandVeryHighDescription:
        "{{resolution}} at {{fps}} FPS is resource hungry and may heavily use the encoder and available upload capacity.",
    },
    focused: {
      member: "Member",
      spectators: "Spectators",
      noSpectators: "No one is watching yet.",
      connectionQuality: "Connection quality",
      healthy: "Healthy",
      actionNeeded: "Action needed",
      degraded: "Degraded",
      volumeControls: "Volume controls",
      volume: "Volume",
      volumePercent: "Volume {{value}}%",
      muted: "Muted",
      muteStream: "Mute stream",
      unmuteStream: "Unmute stream",
      mute: "Mute",
      unmute: "Unmute",
      pip: "Picture-in-Picture",
      fullscreen: "Full screen",
      enterFullscreen: "Enter full screen",
      closeStream: "Close focused stream",
      stopWatching: "Stop watching",
      stopWatchingName: "Stop watching {{name}}",
      hideCards: "Hide participant cards",
      showCards: "Show participant cards",
      streamVolume: "Stream volume",
      noSharedAudio: "This stream is not sharing audio",
    },
    connection: {
      details: "Connection details",
      videoDetails: "Video details",
      audioDetails: "Audio details",
      testingDirect: "Testing the direct connection…",
      retryingDirect: "Retrying the direct connection ({{attempt}}/{{max}})…",
      directUnavailable:
        "A direct connection could not be established. This network may require a relay.",
      collecting: "Collecting connection details…",
      establishing: "The connection is still being established. Details will appear automatically.",
      temporarilyUnavailable: "Connection details are temporarily unavailable: {{error}}",
      measuringQuality: "Measuring connection quality",
      collectingMeasurements: "Collecting measurements",
      gatheringStats: "WebRTC is still gathering connection statistics.",
      livekitManaged: "Managed by the server",
      serverConnection: "Connection to the server",
      voiceConnected: "Voice connected",
      connectingStatus: "Connecting...",
      reconnectingStatus: "Reconnecting...",
      publisherPath: "Browser → Server",
      subscriberPath: "Server → browser",
      participantQuality: "Participant connection quality",
      direction: "Direction",
      publishing: "Publishing",
      receiving: "Receiving",
      signaling: "Signaling",
      connectionState: "Connection state",
      iceState: "ICE state",
      selectedPath: "Selected path",
      transport: "Transport",
      codec: "Codec",
      target: "Target",
      actual: "Actual",
      frames: "Frames",
      audienceSize: "Audience size",
      bitrateCap: "Applied bitrate cap",
      fpsCap: "Applied FPS cap",
      resolutionScale: "Resolution scale",
      videoStreams: "Video streams",
      captureFps: "Captured frame rate",
      encodedFps: "Encoded frame rate",
      decodedFps: "Decoded frame rate",
      renderedFps: "Rendered frame rate",
      freezes: "Viewer freezes · duration",
      bufferDelay: "Average receive buffer delay",
      downloadDiagnostics: "Download recent media diagnostics",
      audioCodec: "Audio codec",
      audioChannels: "Audio channels",
      audioSampleRate: "Audio sample rate",
      audioBitrate: "Audio bitrate",
      audioPacketsLost: "Audio packets lost",
      audioPacketLoss: "Audio packet loss",
      audioJitter: "Audio jitter",
      concealedAudio: "Concealed audio",
      concealedAudioValue: "{{samples}} samples ({{percent}}%) · {{events}} events",
      qualityLimitation: "Quality limitation",
      encodeTime: "Encode time",
      packetsLost: "Packets lost",
      packetLoss: "Packet loss",
      jitter: "Jitter",
      roundTrip: "Round trip",
      localCandidate: "Local candidate",
      remoteCandidate: "Remote candidate",
      availableOutgoingBitrate: "Available outgoing bitrate",
      clientVersion: "Client version",
      serverVersion: "Server version",
      negotiating: "Negotiating",
      unknownCodec: "unknown codec",
      resolutionPending: "resolution pending",
      fpsPending: "fps pending",
      downscale: "{{value}}× downscale",
      noneUnavailable: "none / unavailable",
      state: {
        new: "New",
        offline: "Offline",
        reconnecting: "Reconnecting",
        connecting: "Connecting",
        connected: "Connected",
        disconnected: "Disconnected",
        failed: "Failed",
        closed: "Closed",
        checking: "Checking",
        completed: "Completed",
        stable: "Stable",
        haveLocalOffer: "Local offer created",
        haveRemoteOffer: "Remote offer received",
        haveLocalPranswer: "Local provisional answer",
        haveRemotePranswer: "Remote provisional answer",
      },
    },
    tile: {
      waitingToResume: "Waiting for {{name}} to resume",
      sharingStopped: "Sharing stopped.",
      focused: "Focused.",
      focus: "Focus {{name}}'s {{kind}}",
      returnFrom: "Return from {{name}}'s {{kind}}",
      stoppedFocus: "{{action}} {{name}}'s stopped {{kind}}",
    },
    diagnostics: {
      frameRate: "Frame rate",
      encoder: "Encoder",
      uploadCapacity: "Upload capacity",
      viewerPath: "Viewer path",
      cpuTitle: "Encoder is CPU-limited",
      bandwidthTitle: "Sender upload is constrained",
      networkTitle: "Viewer network path is unstable",
      configuredTitle: "Configured bitrate cap is being reached",
      browserTitle: "Browser reports another quality limit",
      framerateTitle: "Frame rate is below target",
      gatheringTitle: "Collecting measurements",
      healthyTitle: "No bottleneck detected",
      cpuLimited: "CPU limited",
      bitrateAvailable: "{{value}} available",
      packetLoss: "{{value}}% loss",
      packetLossLabel: "Packet loss",
      codec: "Codec",
      receiveRate: "Receive rate",
      uploadRate: "Upload rate",
      latency: "Latency",
      publisherGatheringTitle: "Measuring the connection to the server",
      publisherNetworkTitle: "The connection to the server is unstable",
      publisherHealthyTitle: "The connection to the server is stable",
    },
    events: {
      signalingReconnected: "Signaling reconnected",
      signalingJoined: "Joined signaling",
      signalingTransportError: "Signaling transport error",
      signalingRetry: "Signaling disconnected; retrying in {{seconds}}s",
      signalingRestarting:
        "An update is being deployed; reconnecting signaling without interrupting media",
      sessionExpiredWithoutReload:
        "Your session expired. The page was kept open to avoid interrupting active media.",
      serverVersionChanged: "Connected to application server {{version}}",
      channelSynchronized_one: "Channel synchronized with {{count}} other person",
      channelSynchronized_other: "Channel synchronized with {{count}} other people",
      participantJoined: "{{name}} joined the channel",
      participantLeft: "{{name}} left the channel",
      selectedUnavailable: "A selected user is no longer available",
      server: "Server: {{message}}",
      signalingError: "Signaling error: {{error}}",
      automaticallyWatchingCamera: "Automatically watching {{name}}'s camera",
      publishingState: "Publishing to {{name}}: {{state}}",
      watchingState: "Watching {{name}}: {{state}}",
      publishingOfferSent: "Publishing offer sent to {{name}}",
      rebalanceFailed: "Could not rebalance publishing quality: {{error}}",
      updateFailed: "Could not update {{name}}: {{error}}",
      encodingPreferenceFailed: "Browser did not apply one encoding preference: {{error}}",
      reestablishing: "Reestablishing the stream to {{name}} after resume",
      reestablishFailed: "Could not reestablish {{name}}: {{error}}",
      directConnectionRetry: "Retrying the direct connection to {{name}} ({{attempt}}/{{max}})",
      directConnectionRetryFailed: "Could not retry the direct connection to {{name}}: {{error}}",
      directConnectionUnavailable: "No direct connection to {{name}} could be established",
      screenStartFailed: "Screen capture was not started: {{error}}",
      cameraStartFailed: "Camera was not started: {{error}}",
      pipUnavailable: "Picture-in-Picture is unavailable until the focused video is playing",
      pipFailed: "Picture-in-Picture could not start: {{error}}",
      settingsFailed: "{{source}} settings could not be applied: {{error}}",
    },
  },
};

const ptBR = {
  translation: {
    app: { title: "Sala ao Vivo" },
    join: {
      title: "Entrar na sala ao vivo",
      description: "Encontre sua galera, crie uma sala e compartilhe sua tela ou câmera.",
      displayName: "Nome de exibição",
      placeholder: "Seu nome",
      submit: "Entrar na sala",
    },
    auth: {
      suspended: {
        title: "Sua conta está suspensa",
        description:
          "Esta conta não tem mais acesso ao Live Room. Você pode voltar à tela de acesso para usar outra conta.",
      },
      signupsClosed: {
        title: "Cadastros encerrados",
        description:
          "Não estamos aceitando novas contas no momento. Tente novamente mais tarde. Se você já tem uma conta, ainda pode entrar.",
      },
      backToSignIn: "Voltar à tela de acesso",
      checkingSession: "Verificando sua sessão…",
      signingIn: "Entrando…",
      privacy:
        "O app usa o ID da sua Conta do Google, nome, foto e e-mail verificado. Seu e-mail não é exibido para outros participantes.",
      continueWithGoogle: "Continuar com o Google",
      developmentBadge: "Desenvolvimento",
      developmentTitle: "Simular Conta do Google",
      developmentDescription: "Crie uma identidade de teste local para esta aba do navegador.",
      developmentPrivacy:
        "As identidades de desenvolvimento ficam neste servidor local e cada aba pode usar um perfil diferente.",
      testEmail: "E-mail de teste",
      testEmailHint: "Gerado automaticamente a partir do seu nome para esta conta de teste.",
      googleAccount: "Conta do Google",
      mockAccount: "Conta de teste de desenvolvimento",
      accountMenu: "Menu da conta",
      signOut: "Sair",
      notificationSounds: "Sons de notificação",
      errors: {
        default: "Não foi possível entrar. Tente novamente.",
        "authentication-failed": "Não foi possível verificar o login com o Google.",
        "google-script-unavailable":
          "Não foi possível carregar o login do Google. Verifique sua conexão e tente novamente.",
        "auth-not-configured": "O login com o Google não está configurado neste servidor.",
        "invalid-google-credential":
          "O Google não conseguiu verificar esta conta. Tente novamente.",
        "invalid-development-profile": "Informe um nome de exibição válido.",
      },
    },
    stage: {
      waitingTitle: "Aguardando participantes",
      waitingDescription: "As telas e câmeras dos participantes aparecerão aqui.",
      participants: "Participantes da sala",
      connecting: "Conectando...",
      roomControls: "Controles da sala",
    },
    callBar: {
      label: "Chamada em andamento",
      return: "Voltar à chamada",
      switchTitle: "Sair da chamada atual?",
      switchBody: "Você ainda está em {{name}}. Entrar em outra sala encerra essa chamada.",
      switchBodyUnnamed: "Entrar em outra sala encerra sua chamada atual.",
      stay: "Ficar",
      switchConfirm: "Sair e entrar",
    },
    context: {
      watchParticipantScreen: "Assistir à tela de {{name}}",
      participantAvatar: "{{name}}",
    },
    updates: {
      available: "Atualização disponível",
      ready: "Recarregue quando quiser para usar a versão mais recente.",
      sharing: "Atualizar agora recarregará a página e encerrará seu compartilhamento atual.",
      watching: "Atualizar agora recarregará a página e interromperá o que você está assistindo.",
      apply: "Atualizar agora",
      dismiss: "Dispensar atualização",
    },
    common: {
      camera: "Câmera",
      microphone: "Microfone",
      screen: "Tela",
      screenShare: "Compartilhamento de tela",
      inRoom: "Na sala",
      live: "AO VIVO",
      cancel: "Cancelar",
      continue: "Continuar",
      applyChanges: "Aplicar alterações",
      close: "Fechar",
      unavailable: "Indisponível",
      collecting: "Coletando",
      measuring: "Medindo",
      automatic: "Automático",
      native: "Nativa",
      person_one: "{{count}} pessoa",
      person_other: "{{count}} pessoas",
      viewer_one: "{{count}} espectador",
      viewer_other: "{{count}} espectadores",
      spectator_one: "{{count}} espectador",
      spectator_other: "{{count}} espectadores",
      p2pLink_one: "{{count}} conexão P2P",
      p2pLink_other: "{{count}} conexões P2P",
      sfuConnection_one: "{{count}} conexão SFU",
      sfuConnection_other: "{{count}} conexões SFU",
      meSuffix: "{{name}} (eu)",
      you: "Você",
    },
    camera: {
      setup: "Configurar câmera",
      options: "Opções da câmera",
      active: "A câmera está ligada",
      adjust: "Ajustar câmera",
      adjustDescription: "Altere a qualidade sem interromper",
      stop: "Desligar câmera",
      stopDescription: "Encerre a transmissão da câmera",
      settingsDescription:
        "Você pode ajustar estas configurações novamente enquanto a câmera estiver ativa.",
      qualityTitle: "Qualidade do vídeo",
      qualityDescription:
        "As configurações padrão equilibradas funcionam bem para a maioria das conexões.",
      started: "Câmera iniciada",
      stopped: "Câmera desligada",
      stoppedTitle: "A câmera foi desligada",
      watch: "Assistir à câmera",
    },
    voice: {
      setup: "Configurar microfone",
      options: "Opções do microfone",
      active: "O microfone está ligado",
      adjust: "Ajustar microfone",
      adjustDescription: "Altere o dispositivo de entrada ou o processamento de voz",
      stop: "Desligar microfone",
      stopDescription: "Pare de enviar sua voz",
      settingsTitle: "Microfone",
      settingsDescription: "Escolha as configurações de entrada usadas ao ligar o microfone.",
      inputTitle: "Entrada",
      inputDescription: "Escolha qual microfone o navegador deve capturar.",
      inputDevice: "Dispositivo de entrada",
      defaultDevice: "Padrão do sistema",
      defaultBadge: "Padrão",
      unnamedDevice: "Microfone {{number}}",
      processingTitle: "Processamento de voz",
      processingDescription: "Escolha como o áudio do microfone é tratado.",
      echoCancellation: "Cancelamento de eco",
      echoCancellationDescription:
        "Reduz o som dos alto-falantes que volta a ser capturado pelo microfone.",
      noiseSuppression: "Supressão de ruído",
      noiseSuppressionDescription:
        "Identifica ruídos constantes e sons do teclado, reduzindo-os enquanto você fala.",
      autoGainControl: "Ganho automático",
      autoGainControlDescription: "Mantém falas baixas e altas em um volume mais consistente.",
      threshold: "Sensibilidade",
      moreSensitive: "Mais sensível",
      lessSensitive: "Menos sensível",
      started: "Microfone ligado",
      stopped: "Microfone desligado",
      startFailed: "Não foi possível ligar o microfone: {{error}}",
      settingsFailed: "Não foi possível aplicar as configurações do microfone: {{error}}",
      permissionPending: "Aguardando a permissão do microfone",
      permissionRequired: "O acesso ao microfone é necessário para habilitar este controle",
      permissionUnavailableWithError: "O acesso ao microfone não está disponível: {{error}}",
      blockedTip:
        "Seu navegador bloqueou o microfone. Para falar, permita o acesso nas configurações do site (ao lado da barra de endereço). Você ainda pode ouvir os outros participantes.",
      notFoundTip:
        "Nenhum microfone foi encontrado. Conecte um microfone ou escolha outro dispositivo nas configurações do microfone e tente novamente.",
      inUseTip:
        "O microfone está em uso ou indisponível no sistema. Feche outros aplicativos que o utilizam ou verifique as configurações de áudio do sistema e tente novamente.",
      failedTip:
        "Não foi possível ligar o microfone. Tente novamente ou escolha outro dispositivo nas configurações do microfone. Você ainda pode ouvir os outros participantes.",
      playbackBlocked:
        "A voz de {{name}} está pronta, mas o navegador exige um clique antes de reproduzir o áudio.",
      enableRoomAudio: "Ativar áudio da sala",
      muteOtherVoices: "Silenciar vozes dos outros participantes",
      unmuteOtherVoices: "Ativar vozes dos outros participantes",
      participantAudioControls: "Controles do microfone do participante",
      participantVolume: "Volume da voz",
      muteParticipant: "Silenciar microfone",
      unmuteParticipant: "Ativar microfone",
      participantMicrophoneUnavailable: "Este participante não está compartilhando o microfone",
      notSending: "Microfone desligado",
      notListeningToOthers: "Não está ouvindo os outros participantes",
    },
    screen: {
      setup: "Configurar compartilhamento de tela",
      share: "Compartilhar tela",
      options: "Opções do compartilhamento de tela",
      active: "A tela está sendo compartilhada",
      adjust: "Ajustar transmissão",
      adjustDescription: "Altere a qualidade e o comportamento",
      stop: "Parar compartilhamento",
      stopDescription: "Encerre a transmissão da tela",
      settingsTitle: "Compartilhamento de tela",
      settingsDescription: "Escolha uma qualidade adequada ao conteúdo e à sua conexão.",
      started: "Compartilhamento de tela iniciado",
      stopped: "Compartilhamento de tela encerrado",
      ownAudioWarningTitle: "O áudio da sala pode ser compartilhado",
      ownAudioWarningDescription:
        "Este navegador não consegue excluir as vozes da sala da captura do sistema. Outros participantes podem ouvir a própria voz; use fones de ouvido para evitar eco.",
      dismissOwnAudioWarning: "Dispensar aviso de captura de áudio",
      pictureTitle: "Imagem",
      pictureDescription: "Valores maiores deixam a imagem mais nítida, mas usam mais upload.",
      stoppedTitle: "Compartilhamento de tela encerrado",
      watch: "Assistir à transmissão",
    },
    settings: {
      resolution: "Resolução",
      frameRate: "Taxa de quadros",
      optimizeFor: "Otimizar para",
      cameraResolution: "Resolução da câmera",
      cameraFrameRate: "Taxa de quadros da câmera",
      screenResolution: "Resolução da tela",
      screenFrameRate: "Taxa de quadros da tela",
      contentOptimization: "Otimização do conteúdo",
      auto: "Auto",
      detail: "Gráficos",
      text: "Texto",
      motion: "Movimento",
      nativeAutomatic: "Nativa / automática",
      browserManaged: "Gerenciado pelo navegador",
      resourceDemand: "Demanda estimada de recursos",
      resourceDemandLow: "Baixa",
      resourceDemandLowDescription:
        "{{resolution}} a {{fps}} FPS deve gerar uma carga leve de codificação e de upload.",
      resourceDemandModerate: "Moderada",
      resourceDemandModerateDescription:
        "{{resolution}} a {{fps}} FPS deve gerar uma carga equilibrada de codificação e de upload.",
      resourceDemandHigh: "Alta",
      resourceDemandHighDescription:
        "{{resolution}} a {{fps}} FPS pode aumentar consideravelmente a carga de codificação e o uso do upload.",
      resourceDemandVeryHigh: "Muito alta",
      resourceDemandVeryHighDescription:
        "{{resolution}} a {{fps}} FPS exige muitos recursos e pode utilizar intensamente o codificador e o upload disponível.",
    },
    focused: {
      member: "Participante",
      spectators: "Espectadores",
      noSpectators: "Ninguém está assistindo ainda.",
      connectionQuality: "Qualidade da conexão",
      healthy: "Estável",
      actionNeeded: "Requer atenção",
      degraded: "Degradada",
      volumeControls: "Controles de volume",
      volume: "Volume",
      volumePercent: "Volume {{value}}%",
      muted: "Sem áudio",
      muteStream: "Silenciar transmissão",
      unmuteStream: "Ativar áudio da transmissão",
      mute: "Silenciar",
      unmute: "Ativar áudio",
      pip: "Picture-in-Picture",
      fullscreen: "Tela cheia",
      enterFullscreen: "Entrar em tela cheia",
      closeStream: "Fechar transmissão em foco",
      stopWatching: "Parar de assistir",
      stopWatchingName: "Parar de assistir {{name}}",
      hideCards: "Ocultar cartões dos participantes",
      showCards: "Mostrar cartões dos participantes",
      streamVolume: "Volume da transmissão",
      noSharedAudio: "Esta transmissão não está compartilhando áudio",
    },
    connection: {
      details: "Detalhes da conexão",
      videoDetails: "Detalhes de vídeo",
      audioDetails: "Detalhes de áudio",
      testingDirect: "Testando a conexão direta…",
      retryingDirect: "Tentando novamente a conexão direta ({{attempt}}/{{max}})…",
      directUnavailable:
        "Não foi possível estabelecer uma conexão direta. Esta rede pode exigir um relay.",
      collecting: "Coletando detalhes da conexão…",
      establishing:
        "A conexão ainda está sendo estabelecida. Os detalhes aparecerão automaticamente.",
      temporarilyUnavailable:
        "Os detalhes da conexão estão temporariamente indisponíveis: {{error}}",
      measuringQuality: "Medindo a qualidade da conexão",
      collectingMeasurements: "Coletando medições",
      gatheringStats: "O WebRTC ainda está coletando estatísticas da conexão.",
      livekitManaged: "Gerenciada pelo servidor",
      serverConnection: "Conexão com o servidor",
      voiceConnected: "Voz conectada",
      connectingStatus: "Conectando...",
      reconnectingStatus: "Reconectando...",
      publisherPath: "Navegador → Servidor",
      subscriberPath: "Servidor → navegador",
      participantQuality: "Qualidade da conexão do participante",
      direction: "Direção",
      publishing: "Enviando",
      receiving: "Recebendo",
      signaling: "Sinalização",
      connectionState: "Estado da conexão",
      iceState: "Estado do ICE",
      selectedPath: "Rota selecionada",
      transport: "Transporte",
      codec: "Codec",
      target: "Alvo",
      actual: "Atual",
      frames: "Quadros",
      audienceSize: "Tamanho do público",
      bitrateCap: "Limite de bitrate aplicado",
      fpsCap: "Limite de FPS aplicado",
      resolutionScale: "Escala de resolução",
      videoStreams: "Transmissões de vídeo",
      captureFps: "Taxa de quadros capturados",
      encodedFps: "Taxa de quadros codificados",
      decodedFps: "Taxa de quadros decodificados",
      renderedFps: "Taxa de quadros exibidos",
      freezes: "Travamentos do vídeo · duração",
      bufferDelay: "Atraso médio do buffer de recepção",
      downloadDiagnostics: "Baixar diagnósticos recentes de mídia",
      audioCodec: "Codec de áudio",
      audioChannels: "Canais de áudio",
      audioSampleRate: "Taxa de amostragem do áudio",
      audioBitrate: "Bitrate do áudio",
      audioPacketsLost: "Pacotes de áudio perdidos",
      audioPacketLoss: "Perda de pacotes de áudio",
      audioJitter: "Jitter do áudio",
      concealedAudio: "Áudio reconstruído",
      concealedAudioValue: "{{samples}} amostras ({{percent}}%) · {{events}} eventos",
      qualityLimitation: "Limitação de qualidade",
      encodeTime: "Tempo de codificação",
      packetsLost: "Pacotes perdidos",
      packetLoss: "Perda de pacotes",
      jitter: "Jitter",
      roundTrip: "Tempo de ida e volta",
      localCandidate: "Candidato local",
      remoteCandidate: "Candidato remoto",
      availableOutgoingBitrate: "Bitrate de saída disponível",
      clientVersion: "Versão do cliente",
      serverVersion: "Versão do servidor",
      negotiating: "Negociando",
      unknownCodec: "codec desconhecido",
      resolutionPending: "resolução pendente",
      fpsPending: "fps pendente",
      downscale: "redução de {{value}}×",
      noneUnavailable: "nenhuma / indisponível",
      state: {
        new: "Nova",
        offline: "Offline",
        reconnecting: "Reconectando",
        connecting: "Conectando",
        connected: "Conectada",
        disconnected: "Desconectada",
        failed: "Falhou",
        closed: "Fechada",
        checking: "Verificando",
        completed: "Concluída",
        stable: "Estável",
        haveLocalOffer: "Oferta local criada",
        haveRemoteOffer: "Oferta remota recebida",
        haveLocalPranswer: "Resposta provisória local",
        haveRemotePranswer: "Resposta provisória remota",
      },
    },
    tile: {
      waitingToResume: "Aguardando {{name}} retomar",
      sharingStopped: "Compartilhamento encerrado.",
      focused: "Em foco.",
      focus: "Focar {{kind}} de {{name}}",
      returnFrom: "Sair do foco de {{kind}} de {{name}}",
      stoppedFocus: "{{action}} {{kind}} encerrado de {{name}}",
    },
    diagnostics: {
      frameRate: "Taxa de quadros",
      encoder: "Codificador",
      uploadCapacity: "Capacidade de upload",
      viewerPath: "Rota do espectador",
      cpuTitle: "O codificador está limitado pela CPU",
      bandwidthTitle: "O upload do remetente está limitado",
      networkTitle: "A rota de rede do espectador está instável",
      configuredTitle: "O limite de bitrate configurado foi atingido",
      browserTitle: "O navegador relata outro limite de qualidade",
      framerateTitle: "A taxa de quadros está abaixo do alvo",
      gatheringTitle: "Coletando medições",
      healthyTitle: "Nenhum gargalo detectado",
      cpuLimited: "Limitado pela CPU",
      bitrateAvailable: "{{value}} disponíveis",
      packetLoss: "{{value}}% de perda",
      packetLossLabel: "Perda de pacotes",
      codec: "Codec",
      receiveRate: "Taxa recebida",
      uploadRate: "Taxa de envio",
      latency: "Latência",
      publisherGatheringTitle: "Medindo a conexão com o servidor",
      publisherNetworkTitle: "A conexão com o servidor está instável",
      publisherHealthyTitle: "A conexão com o servidor está estável",
    },
    events: {
      signalingReconnected: "Sinalização reconectada",
      signalingJoined: "Sinalização conectada",
      signalingTransportError: "Erro no transporte da sinalização",
      signalingRetry: "Sinalização desconectada; nova tentativa em {{seconds}}s",
      signalingRestarting:
        "Uma atualização está sendo implantada; reconectando a sinalização sem interromper a mídia",
      sessionExpiredWithoutReload:
        "Sua sessão expirou. A página foi mantida aberta para não interromper a mídia ativa.",
      serverVersionChanged: "Conectado ao servidor da aplicação {{version}}",
      channelSynchronized_one: "Canal sincronizado com mais {{count}} pessoa",
      channelSynchronized_other: "Canal sincronizado com mais {{count}} pessoas",
      participantJoined: "{{name}} entrou no canal",
      participantLeft: "{{name}} saiu do canal",
      selectedUnavailable: "Um usuário selecionado não está mais disponível",
      server: "Servidor: {{message}}",
      signalingError: "Erro de sinalização: {{error}}",
      automaticallyWatchingCamera: "Assistindo automaticamente à câmera de {{name}}",
      publishingState: "Enviando para {{name}}: {{state}}",
      watchingState: "Assistindo {{name}}: {{state}}",
      publishingOfferSent: "Oferta de transmissão enviada para {{name}}",
      rebalanceFailed: "Não foi possível reequilibrar a qualidade da transmissão: {{error}}",
      updateFailed: "Não foi possível atualizar {{name}}: {{error}}",
      encodingPreferenceFailed: "O navegador não aplicou uma preferência de codificação: {{error}}",
      reestablishing: "Restabelecendo a transmissão para {{name}} após retomar",
      reestablishFailed: "Não foi possível restabelecer a transmissão para {{name}}: {{error}}",
      directConnectionRetry:
        "Tentando novamente a conexão direta com {{name}} ({{attempt}}/{{max}})",
      directConnectionRetryFailed:
        "Não foi possível tentar novamente a conexão direta com {{name}}: {{error}}",
      directConnectionUnavailable: "Não foi possível estabelecer uma conexão direta com {{name}}",
      screenStartFailed: "O compartilhamento de tela não foi iniciado: {{error}}",
      cameraStartFailed: "A câmera não foi iniciada: {{error}}",
      pipUnavailable:
        "O Picture-in-Picture ficará disponível quando o vídeo em foco estiver em reprodução",
      pipFailed: "Não foi possível iniciar o Picture-in-Picture: {{error}}",
      settingsFailed: "Não foi possível aplicar as configurações de {{source}}: {{error}}",
    },
  },
};

export const resources = { en, "pt-BR": ptBR };
